import React, { useEffect, useMemo, useState } from "react";
import { Box, DialogContent, DialogContentText } from "@mui/material";
import Popup from "../../../../../components/common/model/Popup";
import closeIcon from "../../../../../assets/closeModel.png";
import CustomButton from "../../../../../components/common/button/CustomButton";
import StepHeader from "./StepHeader";
import SelectStorageStep from "./SelectStorageStep";
import SelectUnitStep from "./SelectUnitStep";
import { SelectedSite, StorageRow, UnitRow } from "./Types";
import useStyles from "../../../../../components/common/model/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSitesAction,
  getSiteUnitListAction,
} from "../../../../../store/reducers/storageSettingSlice";

const SELECT_STORAGE_STEPS = ["Select Storage", "Select Unit"];
interface ManageSelectStorageProps {
  open: boolean;
  onHide: () => void;
  onAssign: (storage: StorageRow, units: UnitRow[]) => void;
  onStepChange?: (step: number) => void;
  selectedSites: SelectedSite[];
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
  status?: string;
}

const ManageSelectStorage: React.FC<ManageSelectStorageProps> = ({
  open,
  onHide,
  onAssign,
  onStepChange,
  selectedSites,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { sites, siteUnitList, siteUnitPagination } = useSelector(
    (state: any) => state.storageSetting,
  );

  const { singleContractDetail } = useSelector(
    (state: any) => state.contractStorage,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [storageSearch, setStorageSearch] = useState("");
  const [selectedStorage, setSelectedStorage] = useState<StorageRow | null>(
    null,
  );

  const [editableUnits, setEditableUnits] = useState<UnitRow[]>([]);
  const [unitDrafts, setUnitDrafts] = useState<
    Record<string, Partial<UnitRow>>
  >({});
  const [unitCache, setUnitCache] = useState<Record<string, UnitRow>>({});

  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [siteFilterOption, setSiteFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
    status: "Active",
  });

  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
    status: "Available",
  });

  useEffect(() => {
    if (open && siteFilterOption) {
      dispatch(getAllSitesAction(siteFilterOption));
    }
  }, [dispatch, siteFilterOption, open]);

  console.log("sites", sites);

  const filteredStorages = useMemo(
    () =>
      sites.filter((storage: any) =>
        `${storage.site_name} ${storage.site_type}`
          .toLowerCase()
          .includes(storageSearch.toLowerCase()),
      ),
    [sites, storageSearch],
  );

  const units = useMemo(() => editableUnits, [editableUnits]);

  const allUnitsSelected =
    units.length > 0 &&
    units.every((unit) => selectedUnitIds.includes(unit.id));

  const reset = () => {
    setActiveStep(0);
    setStorageSearch("");
    setSelectedStorage(null);
    setEditableUnits([]);
    setUnitDrafts({});
    setUnitCache({});
    setSelectedUnitIds([]);
    setFilterOption((previous) => ({
      ...previous,
      page: 1,
      search: "",
    }));

    setSiteFilterOption((previous) => ({
      ...previous,
      page: 1,
      search: "",
      status: "Available",
    }));
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  useEffect(() => {
    if (!selectedStorage?.id || !Array.isArray(siteUnitList)) return;

    const existingSite = selectedSites.find(
      (site) => String(site.siteId) === String(selectedStorage.id),
    );
    const savedUnits = new Map(
      (existingSite?.units ?? []).map((unit: any) => [
        String(unit.unitId ?? unit.id),
        unit,
      ]),
    );

    const getFirstValue = (...values: any[]) =>
      values.find(
        (value) => value !== null && value !== undefined && value !== "",
      );
    const normalizeTime = (value: any) => {
      if (!value) return "";
      const match = String(value).match(/\d{1,2}:\d{2}/);
      return match?.[0] ?? "";
    };

    const mergedUnits: UnitRow[] = siteUnitList.map((unit: any) => {
      const unitId = String(unit.id);
      const savedUnit = savedUnits.get(unitId);
      const draft = unitDrafts[unitId];

      const merged: UnitRow = {
        ...unit,
        inDate:
          getFirstValue(
            savedUnit?.inDate,
            savedUnit?.in_date,
            unit.inDate,
            unit.in_date,
            unit.in,
          ) ?? "",

        inTime: normalizeTime(
          getFirstValue(
            savedUnit?.inTime,
            savedUnit?.in_time,
            unit.inTime,
            unit.in_time,
          ),
        ),

        outDate:
          getFirstValue(
            savedUnit?.outDate,
            savedUnit?.out_date,
            unit.outDate,
            unit.out_date,
            unit.out,
          ) ?? "",

        outTime: normalizeTime(
          getFirstValue(
            savedUnit?.outTime,
            savedUnit?.out_time,
            unit.outTime,
            unit.out_time,
          ),
        ),

        provisional: getFirstValue(
          savedUnit?.provisional,
          savedUnit?.is_provisional,
          unit.provisional,
          unit.is_provisional,
          false,
        ),

        pricePerUnit: getFirstValue(
          savedUnit?.pricePerUnit,
          savedUnit?.price_per_unit,
          unit.pricePerUnit,
          unit.price_per_unit,
        ),

        rentType: getFirstValue(
          savedUnit?.rentType,
          savedUnit?.rent_type,
          unit.rentType,
          unit.rent_type,
        ),

        invoicedUpto:
          getFirstValue(
            savedUnit?.invoicedUpto,
            savedUnit?.invoicedUpTo,
            unit.invoicedUpto,
            unit.invoicedUpTo,
            unit.invoiced_upto,
          ) ?? null,
      };

      return {
        ...merged,
        ...draft,
      };
    });

    // Update only the currently displayed page.
    setEditableUnits(mergedUnits);
    // Preserve unit records loaded from previous pages.
    setUnitCache((previous) => ({
      ...previous,
      ...Object.fromEntries(mergedUnits.map((unit) => [String(unit.id), unit])),
    }));
  }, [selectedStorage?.id, selectedSites, siteUnitList, unitDrafts]);

  useEffect(() => {
    if (selectedStorage?.id) {
      dispatch(
        getSiteUnitListAction({
          ...filterOption,
          siteId: selectedStorage?.id,
        }),
      );
    }
  }, [dispatch, selectedStorage]);

  const handleSelectStorage = (storageId: string) => {
    const storage =
      sites.find((item: any) => String(item.id) === String(storageId)) ?? null;

    if (!storage) return;

    const existingSite = selectedSites.find(
      (site) => String(site.siteId) === String(storage.id),
    );

    const savedUnitIds = (existingSite?.units ?? []).map((unit: any) =>
      String(unit.unitId ?? unit.id),
    );

    setSelectedStorage(storage);
    setEditableUnits([]);
    setUnitDrafts({});
    setUnitCache({});
    setSelectedUnitIds(savedUnitIds);
    setFilterOption((previous) => ({
      ...previous,
      page: 1,
    }));

    setActiveStep(1);
    onStepChange?.(2);
  };

  const handleToggleAllUnits = () => {
    const availableUnits = units.filter(
      (unit) => unit.status?.toLowerCase() === "available",
    );

    const allAvailableSelected =
      availableUnits.length > 0 &&
      availableUnits.every((unit) => selectedUnitIds.includes(unit.id));

    if (allAvailableSelected) {
      // Deselect only available units
      setSelectedUnitIds((prev) =>
        prev.filter((id) => !availableUnits.some((unit) => unit.id === id)),
      );
    } else {
      // Select only available units
      setSelectedUnitIds((prev) => {
        const availableIds = availableUnits.map((unit) => unit.id);

        return Array.from(new Set([...prev, ...availableIds]));
      });
    }
  };

  const handleToggleUnit = (unitId: string) => {
    setSelectedUnitIds((current) =>
      current.includes(unitId)
        ? current.filter((id) => id !== unitId)
        : [...current, unitId],
    );
  };

  const handleBackToStorage = () => {
    setSelectedStorage(null);
    setEditableUnits([]);
    setUnitDrafts({});
    setUnitCache({});
    setSelectedUnitIds([]);
    setActiveStep(0);
    onStepChange?.(1);
  };

  const handleAssign = () => {
    if (!selectedStorage || selectedUnitIds.length === 0) return;

    const allUnits = new Map<string, UnitRow>();

    // Previously loaded pages.
    Object.entries(unitCache).forEach(([id, unit]) => {
      allUnits.set(String(id), unit);
    });

    // Latest values from the current page.
    editableUnits.forEach((unit) => {
      allUnits.set(String(unit.id), unit);
    });

    // Apply the latest user-entered values.
    const assignedUnits = selectedUnitIds
      .map((id) => {
        const unit = allUnits.get(String(id));

        if (!unit) return undefined;

        return {
          ...unit,
          ...unitDrafts[id],
        };
      })
      .filter((unit): unit is UnitRow => Boolean(unit));

    if (assignedUnits.length !== selectedUnitIds.length) {
      console.error("Some selected units are missing from the loaded data.");
      return;
    }

    onStepChange?.(3);
    onAssign(selectedStorage, assignedUnits);
    handleClose();
  };

  const handleUnitChange = (
    unitId: string,
    field: keyof UnitRow,
    value: string | boolean | null,
  ) => {
    setEditableUnits((current) =>
      current.map((unit) =>
        String(unit.id) === String(unitId) ? { ...unit, [field]: value } : unit,
      ),
    );

    setUnitCache((previous) => {
      const currentUnit =
        previous[unitId] ??
        editableUnits.find((unit) => String(unit.id) === String(unitId));

      if (!currentUnit) return previous;

      return {
        ...previous,
        [unitId]: {
          ...currentUnit,
          [field]: value,
        },
      };
    });

    setUnitDrafts((previous) => ({
      ...previous,
      [unitId]: {
        ...previous[unitId],
        [field]: value,
      },
    }));
  };

  const displayedUnits = useMemo(
    () =>
      units.map((unit) => ({
        ...unit,
        ...unitDrafts[unit.id],
      })),
    [units, unitDrafts],
  );

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <SelectStorageStep
          storages={filteredStorages}
          search={storageSearch}
          onSearchChange={setStorageSearch}
          onSelectStorage={handleSelectStorage}
          filterOption={siteFilterOption}
          setFilterOption={setSiteFilterOption}
        />
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <CustomButton
              title="Back"
              variant="outlined"
              onClick={handleBackToStorage}
            />
            <Box sx={{ fontSize: "16px", fontWeight: 700 }}>
              {selectedStorage?.name} Units
            </Box>
            <CustomButton
              title="Assign Unit"
              disabled={selectedUnitIds.length === 0}
              onClick={handleAssign}
            />
          </Box>
          <SelectUnitStep
            contractData={singleContractDetail || {}}
            onFieldChange={() => {}}
            units={displayedUnits}
            selectedStorage={selectedStorage}
            pagination={siteUnitPagination}
            selectedUnitIds={selectedUnitIds}
            allUnitsSelected={allUnitsSelected}
            onToggleUnit={handleToggleUnit}
            onToggleAllUnits={handleToggleAllUnits}
            onContinue={handleAssign}
            onCancel={() => setActiveStep(0)}
            onUnitChange={handleUnitChange}
          />
        </>
      );
    }

    return null;
  };

  return (
    <Popup
      open={open}
      title="Manage Select Storage"
      onHide={handleClose}
      fullWidth
      maxWidth="xl"
      popupBg="#FFFFFF"
      header={
        <img
          onClick={handleClose}
          src={closeIcon}
          className={classes.closeicons}
          style={{ cursor: "pointer" }}
          alt="close"
        />
      }
    >
      <DialogContent sx={{ p: 2 }}>
        <DialogContentText component="div">
          <Box sx={{ mb: 2, overflowX: "auto" }}>
            <StepHeader activeStep={activeStep} steps={SELECT_STORAGE_STEPS} />
          </Box>
          {renderStep()}
        </DialogContentText>
      </DialogContent>
    </Popup>
  );
};

export default ManageSelectStorage;
